import React, { useState, useEffect } from 'react';
import { Compass, BookOpen, Briefcase, Star, Award, ChevronRight, RotateCcw, BarChart } from 'lucide-react';
// Define interfaces for your data structures
interface Answer {
  approach?: 'interest' | 'career';
  [key: string]: any;
}

interface QuestionOption {
  value: string;
  label: string;
  icon?: string | React.ReactElement; // Make icon optional with proper typing
}

interface Question {
  id: string;
  question: string; // Changed from "text" to match actual usage
  subtitle?: string; // Add this field since it's used
  options: QuestionOption[];
  next: (answer: string) => string;
}


interface StreamResult {
  stream: string;
  tagline: string;
  subjects: string;
  icon: React.ReactElement;
  color: 'blue' | 'green' | 'purple' | 'amber' | 'orange' | 'red';
  description: string;
  careers: string[];
  entranceExams: string;
  colleges: string;
  successProfile: string;
  skillsDeveloped: string;
}

const EnhancedStreamSelectionGuide = () => {
  const [currentStep, setCurrentStep] = useState('welcome');
  const [answers, setAnswers] = useState<Answer>({});
  const [result, setResult] = useState<StreamResult | null>(null);

  const [animation, setAnimation] = useState(false);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    // Animate transitions
    setAnimation(true);
    const timer = setTimeout(() => setAnimation(false), 500);
    
    // Calculate progress
    if (currentStep === 'welcome') {
      setProgress(0);
    } else if (currentStep === 'start') {
      setProgress(10);
    } else if (currentStep === 'result') {
      setProgress(100);
    } else if (currentStep === 'interest_questions' || currentStep === 'career_questions') {
      setProgress(20);
    } else if (answers.approach === 'interest') {
      const index = interestQuestions.findIndex(q => q.id === currentStep);
      if (index >= 0) {
        setProgress(20 + ((index + 1) / interestQuestions.length) * 70);
      }
    } else if (answers.approach === 'career') {
      const index = careerQuestions.findIndex(q => q.id === currentStep);
      if (index >= 0) {
        setProgress(20 + ((index + 1) / careerQuestions.length) * 70);
      }
    }
    
    return () => clearTimeout(timer);
  }, [currentStep, answers.approach]);
  
  // Enhanced approach options with icons
  const approachOptions = [
    { 
      id: 'interest', 
      label: 'Based on Your Interests', 
      description: 'Choose a stream based on subjects you enjoy and excel at',
      icon: <Star className="w-8 h-8 text-amber-500" />
    },
    { 
      id: 'career', 
      label: 'Based on Career Goals', 
      description: 'Choose a stream that aligns with your future career aspirations',
      icon: <Briefcase className="w-8 h-8 text-purple-500" />
    }
  ];
  
  // Interest-based questions
  const interestQuestions = [
    {
      id: 'maths_physics',
      question: 'Do you enjoy Mathematics and Physics?',
      subtitle: 'These subjects involve calculations, formulas, and understanding physical laws',
      options: [
        { value: 'yes', label: 'Yes, I find them interesting' },
        { value: 'somewhat', label: 'Somewhat, I can manage them' },
        { value: 'no', label: 'No, I find them difficult' }
      ],
      next: (answer:any) => answer === 'yes' ? 'business_finance' : (answer === 'somewhat' ? 'programming' : 'biology')
    },
    {
      id: 'business_finance',
      question: 'Are you interested in Business and Finance?',
      subtitle: 'These involve understanding markets, money management, and commercial concepts',
      options: [
        { value: 'yes', label: 'Yes, very much' },
        { value: 'somewhat', label: 'Somewhat interested' },
        { value: 'no', label: 'Not really' }
      ],
      next: (answer:any) => answer === 'yes' ? 'result_commerce_maths' : (answer === 'somewhat' ? 'commerce_or_science' : 'programming')
    },
    {
      id: 'commerce_or_science',
      question: 'Would you prefer working with business concepts or scientific principles?',
      subtitle: 'This helps determine if you lean more toward commerce or pure science',
      options: [
        { value: 'business', label: 'Business concepts' },
        { value: 'science', label: 'Scientific principles' }
      ],
      next: (answer:any) => answer === 'business' ? 'result_commerce_maths' : 'result_pcm'
    },
    {
      id: 'programming',
      question: 'Do you enjoy problem-solving and logical thinking?',
      subtitle: 'These skills are important for computer science, engineering, and analytics',
      options: [
        { value: 'yes', label: 'Yes, definitely' },
        { value: 'somewhat', label: 'To some extent' },
        { value: 'no', label: 'Not particularly' }
      ],
      next: (answer:any) => answer === 'no' ? 'arts_creativity' : 'result_pcm'
    },
    {
      id: 'biology',
      question: 'Are you interested in Biology and life sciences?',
      subtitle: 'These involve studying living organisms, health sciences, and natural processes',
      options: [
        { value: 'yes', label: 'Yes, very interested' },
        { value: 'somewhat', label: 'Somewhat interested' },
        { value: 'no', label: 'Not really' }
      ],
      next: (answer:any) => answer === 'no' ? 'arts_creativity' : (answer === 'somewhat' ? 'medicine_interest' : 'result_pcb')
    },
    {
      id: 'medicine_interest',
      question: 'Are you interested in medicine or healthcare as a career?',
      subtitle: 'This includes being a doctor, nurse, pharmacist, etc.',
      options: [
        { value: 'yes', label: 'Yes, definitely' },
        { value: 'no', label: 'Not really' }
      ],
      next: (answer:any) => answer === 'yes' ? 'result_pcb' : 'arts_creativity'
    },
    {
      id: 'arts_creativity',
      question: 'Do you enjoy creative subjects, literature, or social sciences?',
      subtitle: 'These include history, geography, languages, arts, and cultural studies',
      options: [
        { value: 'yes', label: 'Yes, very much' },
        { value: 'somewhat', label: 'To some extent' },
        { value: 'no', label: 'Not particularly' }
      ],
      next: (answer:any) => answer === 'yes' ? 'result_humanities' : (answer === 'somewhat' ? 'humanities_vs_commerce' : 'result_commerce')
    },
    {
      id: 'humanities_vs_commerce',
      question: 'Which appeals to you more?',
      subtitle: 'This will help determine if humanities or commerce is a better fit',
      options: [
        { value: 'creative', label: 'Creative expression and social understanding' },
        { value: 'practical', label: 'Practical business applications and economics' }
      ],
      next: (answer:any) => answer === 'creative' ? 'result_humanities' : 'result_commerce'
    }
  ];
  
  // Career-based questions
  const careerQuestions = [
    {
      id: 'career_category',
      question: 'Which career field are you most drawn to?',
      subtitle: 'Select the area that most appeals to your future aspirations',
      options: [
        { value: 'engineering', label: 'Engineering / Technology', icon: '🔧' },
        { value: 'medical', label: 'Medical / Healthcare', icon: '⚕️' },
        { value: 'business', label: 'Business / Management', icon: '📊' },
        { value: 'arts', label: 'Arts / Design / Social Sciences', icon: '🎨' },
        { value: 'unsure', label: "I'm not sure yet", icon: '❓' }
      ],
      next: (answer:any) => {
        switch(answer) {
          case 'engineering': return 'engineering_type';
          case 'medical': return 'medical_type';
          case 'business': return 'business_type';
          case 'arts': return 'arts_type';
          case 'unsure': return 'aptitude_check';
          default: return 'aptitude_check';
        }
      }
    },
    {
      id: 'engineering_type',
      question: 'What type of engineering interests you the most?',
      subtitle: 'Different engineering fields require slightly different skill sets',
      options: [
        { value: 'software', label: 'Software / Computer Science', icon: '💻' },
        { value: 'mechanical', label: 'Mechanical / Civil / Electrical', icon: '🔩' },
        { value: 'chemical', label: 'Chemical / Biotech', icon: '🧪' }
      ],
      next: () => 'result_pcm'
    },
    {
      id: 'medical_type',
      question: 'Which aspect of healthcare interests you?',
      subtitle: 'This helps determine your specific medical career path',
      options: [
        { value: 'doctor', label: 'Doctor / Surgeon', icon: '👨‍⚕️' },
        { value: 'research', label: 'Medical Research / Biotech', icon: '🔬' },
        { value: 'allied', label: 'Pharmacy / Nursing / Allied Health', icon: '💊' }
      ],
      next: (answer:any) => answer === 'research' ? 'pcb_or_pcmb' : 'result_pcb'
    },
    {
      id: 'pcb_or_pcmb',
      question: 'Would you like to keep your options open for both medical and engineering?',
      subtitle: 'This would require taking both Mathematics and Biology',
      options: [
        { value: 'yes', label: 'Yes, I want maximum flexibility' },
        { value: 'no', label: 'No, I prefer to focus on biology' }
      ],
      next: (answer:any) => answer === 'yes' ? 'result_pcmb' : 'result_pcb'
    },
    {
      id: 'business_type',
      question: 'Which business area appeals to you most?',
      subtitle: 'Different business careers require different skills',
      options: [
        { value: 'finance', label: 'Finance / Accounting / Banking', icon: '💰' },
        { value: 'management', label: 'Management / Entrepreneurship', icon: '📈' },
        { value: 'analytics', label: 'Business Analytics / Economics', icon: '📊' }
      ],
      next: (answer:any) => answer === 'analytics' ? 'result_commerce_maths' : 'result_commerce'
    },
    {
      id: 'arts_type',
      question: 'Which humanities or arts field interests you most?',
      subtitle: 'This helps determine your specific humanities path',
      options: [
        { value: 'social', label: 'Social Sciences / Law / Psychology', icon: '📚' },
        { value: 'creative', label: 'Creative Arts / Design / Media', icon: '🎭' },
        { value: 'languages', label: 'Languages / Literature / Journalism', icon: '✒️' }
      ],
      next: () => 'result_humanities'
    },
    {
      id: 'aptitude_check',
      question: 'Which subjects do you typically score highest in?',
      subtitle: 'Your academic strengths can indicate which stream might suit you best',
      options: [
        { value: 'maths_science', label: 'Mathematics and Science' },
        { value: 'business_accounts', label: 'Business Studies and Accounts' },
        { value: 'languages_arts', label: 'Languages and Social Sciences' },
        { value: 'mixed', label: 'A mix of different subjects' }
      ],
      next: (answer:any) => {
        switch(answer) {
          case 'maths_science': return 'sci_preference';
          case 'business_accounts': return 'result_commerce';
          case 'languages_arts': return 'result_humanities';
          case 'mixed': return 'flexibility_question';
          default: return 'flexibility_question';
        }
      }
    },
    {
      id: 'sci_preference',
      question: 'Within science, which area interests you more?',
      subtitle: 'This will help determine whether PCM or PCB is more suitable',
      options: [
        { value: 'physics_chem', label: 'Physics and Chemistry' },
        { value: 'biology', label: 'Biology and Life Sciences' },
        { value: 'both', label: 'Both equally' }
      ],
      next: (answer:any) => {
        switch(answer) {
          case 'physics_chem': return 'result_pcm';
          case 'biology': return 'result_pcb';
          case 'both': return 'result_pcmb';
          default: return 'result_pcmb';
        }
      }
    },
    {
      id: 'flexibility_question',
      question: 'Do you prefer keeping your options open for various career paths?',
      subtitle: 'This determines whether you need a more flexible or specialized approach',
      options: [
        { value: 'yes', label: 'Yes, I want maximum flexibility' },
        { value: 'no', label: 'No, I prefer to specialize' }
      ],
      next: (answer:any) => answer === 'yes' ? 'result_pcmb' : 'working_style'
    },
    {
      id: 'working_style',
      question: 'How do you prefer to work?',
      subtitle: 'Your working style can indicate which stream might be most enjoyable',
      options: [
        { value: 'analytical', label: 'Analyzing data and solving problems' },
        { value: 'creative', label: 'Creating and expressing ideas' },
        { value: 'people', label: 'Working with and helping people' }
      ],
      next: (answer:any) => {
        switch(answer) {
          case 'analytical': return 'result_pcm';
          case 'creative': return 'result_humanities';
          case 'people': return 'result_pcb';
          default: return 'result_pcm';
        }
      }
    }
  ];
  
  // Enhanced stream results with icons and more detailed information
  const streamResults: { [key: string]: StreamResult } = {
    result_pcm: {
      stream: 'Science (PCM)',
      tagline: 'The path to engineering and technical fields',
      subjects: 'Physics, Chemistry, Mathematics',
      icon: <Compass className="w-12 h-12 text-blue-500" />,
      color: 'blue',
      description: 'Perfect for students interested in engineering, computer science, architecture, or pure sciences. This stream develops strong analytical and problem-solving skills that are valued across many technical fields.',
      careers: [
        'Engineering (Civil, Mechanical, Electrical, Computer)',
        'Information Technology & Computer Science',
        'Data Science & Analytics',
        'Architecture & Design',
        'Finance & Quantitative Analysis'
      ],
      entranceExams: 'JEE (Main & Advanced), BITSAT, MET, VITEEE, COMEDK, KCET',
      colleges: 'IITs, NITs, BITS, VIT, DTU, COEPs, and many other engineering institutions',
      successProfile: 'Students who excel in PCM typically enjoy problem-solving, have good mathematical aptitude, and can apply theoretical concepts to practical situations.',
      skillsDeveloped: 'Analytical thinking, Problem-solving, Logical reasoning, Mathematical modeling, Technical expertise'
    },
    result_pcb: {
      stream: 'Science (PCB)',
      tagline: 'The gateway to medicine and life sciences',
      subjects: 'Physics, Chemistry, Biology',
      icon: <BookOpen className="w-12 h-12 text-green-500" />,
      color: 'green',
      description: 'Ideal for students interested in medicine, dentistry, pharmacy, life sciences, or healthcare. This stream builds a strong foundation in understanding living organisms and prepares you for careers in the medical field.',
      careers: [
        'Doctor (MBBS, BDS, BAMS, BHMS)',
        'Veterinary Sciences',
        'Pharmacy & Pharmaceutical Research',
        'Biotechnology & Genetics',
        'Nursing & Allied Health Sciences'
      ],
      entranceExams: 'NEET, AIIMS, JIPMER, IISER, KVPY, AIPVT',
      colleges: 'AIIMS, CMC Vellore, JIPMER, Government and private medical colleges across India',
      successProfile: 'Successful PCB students typically have strong memorization skills, attention to detail, and a genuine interest in understanding biological processes and health sciences.',
      skillsDeveloped: 'Scientific observation, Analytical thinking, Memory retention, Patient care aptitude, Laboratory techniques'
    },
    result_pcmb: {
      stream: 'Science (PCMB)',
      tagline: 'Maximum flexibility for future options',
      subjects: 'Physics, Chemistry, Mathematics, Biology',
      icon: <Award className="w-12 h-12 text-purple-500" />,
      color: 'purple',
      description: 'A challenging but highly flexible option keeping doors open for both engineering and medical fields. This stream requires excellent time management and study skills but offers the widest range of future opportunities.',
      careers: [
        'All careers available to PCM students',
        'All careers available to PCB students',
        'Interdisciplinary fields like Biomedical Engineering',
        'Research in fields requiring both mathematical and biological knowledge',
        'Data Science in healthcare and biotechnology'
      ],
      entranceExams: 'JEE, NEET, and all other PCM/PCB exams',
      colleges: 'Access to both engineering and medical institutions across India',
      successProfile: 'PCMB students are typically academically strong with excellent study habits, time management skills, and broad scientific interests.',
      skillsDeveloped: 'Versatility, Time management, Comprehensive scientific knowledge, Adaptability, Academic resilience'
    },
    result_commerce: {
      stream: 'Commerce',
      tagline: 'The world of business and economics',
      subjects: 'Accountancy, Business Studies, Economics, English',
      icon: <Briefcase className="w-12 h-12 text-amber-500" />,
      color: 'amber',
      description: 'Excellent for students interested in business, finance, management, or entrepreneurship. This stream develops practical business knowledge and prepares you for a wide range of commercial careers.',
      careers: [
        'Chartered Accountancy (CA)',
        'Company Secretary (CS)',
        'Business Management & Administration',
        'Banking & Finance',
        'Entrepreneurship & Business Ownership'
      ],
      entranceExams: 'CAT, XAT, CLAT, CET, IPMAT, CA Foundation, CS Foundation',
      colleges: 'SRCC, LSR, Christ University, Narsee Monjee, Symbiosis, and other top commerce colleges',
      successProfile: 'Successful commerce students typically have good numerical skills, interest in business affairs, practical thinking, and organizational abilities.',
      skillsDeveloped: 'Financial literacy, Business acumen, Economic understanding, Communication skills, Commercial awareness'
    },
    result_commerce_maths: {
      stream: 'Commerce with Mathematics',
      tagline: 'Business knowledge with quantitative edge',
      subjects: 'Accountancy, Business Studies, Economics, Mathematics',
      icon: <BarChart className="w-12 h-12 text-orange-500" />,
      color: 'orange',
      description: 'Combines business knowledge with strong quantitative skills for broader career options. Adding mathematics to commerce opens doors to analytical fields like actuarial science and quantitative finance.',
      careers: [
        'Chartered Accountancy (CA) with quantitative specialization',
        'Actuarial Science',
        'Financial Analysis & Investment Banking',
        'Business Analytics & Data Science',
        'Economic Research & Consulting'
      ],
      entranceExams: 'CAT, XAT, CLAT, CET, IPMAT, CA Foundation, IAI (Actuarial)',
      colleges: 'SRCC, LSR, Christ University, Narsee Monjee, Symbiosis, and other top commerce colleges',
      successProfile: 'Commerce with Mathematics students typically excel in numerical analysis, have strong logical reasoning skills, and can apply mathematical concepts to business scenarios.',
      skillsDeveloped: 'Quantitative analysis, Financial modeling, Statistical reasoning, Business mathematics, Logical problem-solving'
    },
    result_humanities: {
      stream: 'Humanities/Arts',
      tagline: 'Understanding society, culture, and human behavior',
      subjects: 'History, Political Science, Geography, Economics, Psychology, Sociology, Languages',
      icon: <BookOpen className="w-12 h-12 text-red-500" />,
      color: 'red',
      description: 'Perfect for students interested in social sciences, creative fields, law, civil services, or education. This stream develops critical thinking, communication skills, and a deeper understanding of human society.',
      careers: [
        'Law & Legal Services',
        'Civil Services & Government',
        'Journalism & Mass Communication',
        'Psychology & Counseling',
        'Teaching & Education',
        'Art, Design & Creative Fields'
      ],
      entranceExams: 'CLAT, DUET, CUET, NID, NIFT, UPSC CSE, UGC NET',
      colleges: 'St. Stephen\'s, LSR, JNU, Presidency College, Loyola College, Xaviers Colleges',
      successProfile: 'Successful humanities students typically have strong reading and writing skills, critical thinking abilities, creativity, and interest in human behavior and society.',
      skillsDeveloped: 'Critical thinking, Research abilities, Written and verbal communication, Cultural literacy, Social awareness'
    }
  };
  
  const handleOptionSelect = (questionId: string, answer: string) => {
    const updatedAnswers = { ...answers, [questionId]: answer };
    setAnswers(updatedAnswers);
    
    // Find the current question to determine next step
    let currentQuestion;
    if (currentStep === 'interest_questions') {
      currentQuestion = interestQuestions[0];
    } else if (currentStep === 'career_questions') {
      currentQuestion = careerQuestions[0];
    } else {
      if (answers.approach === 'interest') {
        currentQuestion = interestQuestions.find(q => q.id === questionId);
      } else {
        currentQuestion = careerQuestions.find(q => q.id === questionId);
      }
    }
    
    if (currentQuestion) {
      const nextStep = currentQuestion.next(answer);
      if (nextStep.startsWith('result_')) {
        setResult(streamResults[nextStep]);
        setCurrentStep('result');
      } else {
        setCurrentStep(nextStep);
      }
    }
  };
  
  const handleApproachSelect = (approach: any) => {
    setAnswers({ ...answers, approach });
    if (approach === 'interest') {
      setCurrentStep('interest_questions');
    } else {
      setCurrentStep('career_questions');
    }
  };
  
  const startQuiz = () => {
    setCurrentStep('start');
  };
  
  const resetQuiz = () => {
    setCurrentStep('welcome');
    setAnswers({});
    setResult(null);
    setProgress(0);
  };
  
  const renderWelcomeScreen = () => {
    return (
      <div className={`space-y-6 ${animation ? 'animate-fade-in' : ''}`}>
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <Compass className="w-16 h-16 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-blue-900">Stream Selection AI Guide</h1>
          <p className="text-lg text-gray-600">Discover the perfect academic path for your future career</p>
        </div>
        
        <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
          <h2 className="font-bold text-blue-800">How This Guide Works</h2>
          <p className="mt-2 text-gray-700">Answer a series of questions about your interests, strengths, and aspirations. Our AI will analyze your responses and recommend the best stream for you after 10th standard.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="flex justify-center mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-600 font-bold">1</span>
              </div>
            </div>
            <h3 className="font-bold text-gray-800">Answer Questions</h3>
            <p className="text-sm text-gray-600 mt-2">Share your interests and preferences</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="flex justify-center mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-bold">2</span>
              </div>
            </div>
            <h3 className="font-bold text-gray-800">Get Analysis</h3>
            <p className="text-sm text-gray-600 mt-2">Our AI processes your responses</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <div className="flex justify-center mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 font-bold">3</span>
              </div>
            </div>
            <h3 className="font-bold text-gray-800">Receive Recommendation</h3>
            <p className="text-sm text-gray-600 mt-2">Get a personalized stream suggestion</p>
          </div>
        </div>
        
        <div className="flex justify-center mt-6">
          <button 
            onClick={startQuiz}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium flex items-center"
          >
            Start Now <ChevronRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };
  
  const renderQuestion = () => {
    if (currentStep === 'welcome') {
      return renderWelcomeScreen();
    }
    
    if (currentStep === 'result') {
      return null;
    }
    
    if (currentStep === 'start') {
      return (
        <div className={`space-y-6 ${animation ? 'animate-fade-in' : ''}`}>
          <h2 className="text-xl font-bold text-blue-800">How would you like to choose your stream?</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {approachOptions.map(option => (
              <div 
                key={option.id} 
                className="bg-white border-2 border-blue-200 rounded-lg p-5 hover:border-blue-500 cursor-pointer transition-all flex items-start"
                onClick={() => handleApproachSelect(option.id)}
              >
                <div className="mr-4 mt-1">{option.icon}</div>
                <div>
                  <h3 className="font-bold text-lg text-blue-700">{option.label}</h3>
                  <p className="text-gray-600 mt-2">{option.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
  let currentQuestion: Question | undefined;
  let questionList: Question[];
  let questionIndex = 0;
    
  if (currentStep === 'interest_questions') {
    // Show the first interest question
    currentQuestion = interestQuestions[0];
    questionList = interestQuestions;
    questionIndex = 0;
  } else if (currentStep === 'career_questions') {
    // Show the first career question
    currentQuestion = careerQuestions[0];
    questionList = careerQuestions;
    questionIndex = 0;
  } else {
    // Find the specific question
    if (answers.approach === 'interest') {
      questionList = interestQuestions;
      currentQuestion = interestQuestions.find(q => q.id === currentStep);
      questionIndex = interestQuestions.findIndex(q => q.id === currentStep);
    } else {
      questionList = careerQuestions;
      currentQuestion = careerQuestions.find(q => q.id === currentStep);
      questionIndex = careerQuestions.findIndex(q => q.id === currentStep);
    }
  }
    
    if (!currentQuestion) return null;
    
    return (
      <div className={`space-y-6 ${animation ? 'animate-fade-in' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-blue-800">{currentQuestion.question}</h2>
            {currentQuestion.subtitle && (
              <p className="text-gray-500 mt-1">{currentQuestion.subtitle}</p>
            )}
          </div>
          <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
            Question {questionIndex + 1}/{questionList.length}
          </span>
        </div>
        
        <div className="space-y-3">
          {currentQuestion.options.map(option => (
            <div 
              key={option.value} 
              className="bg-white border-2 border-blue-100 rounded-lg p-4 hover:bg-blue-50 cursor-pointer transition-all flex items-center"
              onClick={() => handleOptionSelect(currentQuestion!.id, option.value)}
            >
              <div className="w-5 h-5 rounded-full border-2 border-blue-400 mr-3 flex-shrink-0 flex items-center justify-center">
                {answers[currentQuestion!.id] === option.value && (
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                )}
              </div>
              <div className="flex items-center">
                {option.icon && <span className="mr-2 text-xl">{option.icon}</span>}
                <span className="text-gray-700">{option.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderResult = () => {
    if (!result) return null;
    
    const resultColorClass = {
      'blue': 'border-blue-500 bg-blue-50',
      'green': 'border-green-500 bg-green-50',
      'purple': 'border-purple-500 bg-purple-50',
      'amber': 'border-amber-500 bg-amber-50',
      'orange': 'border-orange-500 bg-orange-50',
      'red': 'border-red-500 bg-red-50'
    }[result.color];
    
    return (
      <div className={`space-y-8 ${animation ? 'animate-fade-in' : ''}`}>
        <div className={`p-6 rounded-lg border-l-4 ${resultColorClass} transition-all`}>
          <div className="flex items-center">
            <div className="mr-4">
              {result.icon}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{result.stream}</h2>
              <p className="text-lg text-gray-600">{result.tagline}</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Key Subjects</h3>
            <p className="text-gray-700">{result.subjects}</p>
            
            <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Overview</h3>
            <p className="text-gray-700">{result.description}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Skills Developed</h3>
            <p className="text-gray-700">{result.skillsDeveloped}</p>
            
            <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3">Success Profile</h3>
            <p className="text-gray-700">{result.successProfile}</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Career Opportunities</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {result.careers.map((career, index) => (
              <li key={index} className="flex items-start">
                <div className="bg-blue-100 rounded-full p-1 mr-2 mt-1">
                  <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">{career}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Common Entrance Exams</h3>
            <p className="text-gray-700">{result.entranceExams}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Notable Colleges</h3>
            <p className="text-gray-700">{result.colleges}</p>
          </div>
        </div>
        
        <div className="flex justify-center mt-6">
          <button 
            onClick={resetQuiz}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium flex items-center"
          >
            <RotateCcw className="mr-2 w-5 h-5" /> Start Again
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      {/* Progress bar */}
      <div className="h-2 w-full bg-gray-200 rounded-full mb-8">
        <div 
          className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      
      {currentStep === 'result' ? renderResult() : renderQuestion()}
    </div>
  );
};

export default EnhancedStreamSelectionGuide;